import {
  AccountCircle,
  Business,
  Chat,
  ChevronLeft,
  ChevronRight,
  Dashboard,
  Description,
  FactCheck,
  FlagOutlined,
  Info,
  Logout,
  Menu as MenuIcon,
  People,
  QuestionAnswer,
  Settings,
  Storage,
} from '@mui/icons-material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { logout } from '../store/slices/authSlice';
import { setSidebarOpen } from '../store/slices/uiSlice';
import { setTheme } from '../store/slices/uiSlice';
import {
  BORDERS,
  DARK_BG,
  GLASS,
  LIGHT_BG,
  PRIMARY,
  SECONDARY,
  SHADOWS,
} from '../theme/palette';
import LanguageSwitcher from './LanguageSwitcher';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const Layout = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector(state => state.auth.user);
  const sidebarOpen = useSelector(state => state.ui.sidebarOpen);
  const themeMode = useSelector(state => state.ui.theme);
  const chatFullscreen = useSelector(state => state.ui.chatFullscreen);

  const version = '1.0.10';

  const handleDrawerToggle = () => {
    dispatch(setSidebarOpen(!sidebarOpen));
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    dispatch(logout());
    navigate('/');
  };

  const navigationItems = [
    {
      text: t('navigation.dashboard'),
      icon: <Dashboard />,
      path: '/',
      roles: ['admin', 'user', 'company_admin', 'company_user'],
      category: 'main',
    },
    {
      text: t('navigation.companies'),
      icon: <Business />,
      path: '/companies',
      roles: ['admin'],
      category: 'management',
    },
    {
      text: t('navigation.users'),
      icon: <People />,
      path: '/users',
      roles: ['admin'],
      category: 'management',
    },
    {
      text: t('navigation.documents'),
      icon: <Description />,
      path: '/documents',
      roles: ['admin'],
      category: 'content',
    },
    {
      text: t('navigation.qaLogs'),
      icon: <QuestionAnswer />,
      path: '/qa-logs',
      roles: ['admin'],
      category: 'ai',
    },
    {
      text: t('navigation.chat'),
      icon: <Chat />,
      path: '/chat',
      roles: ['admin', 'user', 'company_admin', 'company_user'],
      category: 'ai',
    },
    {
      text: t('navigation.reports'),
      icon: <FactCheck />,
      path: '/reports',
      roles: ['admin'],
      category: 'management',
    },
    {
      text: t('navigation.myReports'),
      icon: <FlagOutlined />,
      path: '/my-reports',
      roles: ['admin', 'user', 'company_admin', 'company_user'],
      category: 'main',
    },
    {
      text: t('navigation.vectorStore'),
      icon: <Storage />,
      path: '/vectorstore',
      roles: ['admin'],
      category: 'ai',
    },
    {
      text: t('navigation.account'),
      icon: <Settings />,
      path: '/account',
      roles: ['admin', 'user', 'company_admin', 'company_user'],
      category: 'main',
    },
    {
      text: t('navigation.companyPortal'),
      icon: <Business />,
      path: '/company-portal',
      roles: ['company_admin', 'company_user', 'admin'],
      category: 'main',
    },
  ];

  const currentDrawerWidth = sidebarCollapsed
    ? collapsedDrawerWidth
    : drawerWidth;

  // Group navigation items by category
  const groupedItems = navigationItems
    .filter(item => item.roles.includes(user?.role))
    .reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background:
          theme.palette.mode === 'light' ? LIGHT_BG.gradient : DARK_BG.gradient,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          background: PRIMARY.gradient,
          color: 'white',
          minHeight: 80,
          position: 'relative',
          boxShadow: theme.shadows[8],
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            background: SECONDARY.gradient,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          },
        }}
      >
        {!sidebarCollapsed && (
          <Box>
            <img
              src='/android-chrome-192x192.png'
              alt='ARIA Logo'
              style={{
                width: 64,
                height: 64,
                objectFit: 'contain',
                marginBottom: 4,
              }}
            />
            <Typography
              variant='caption'
              sx={{
                opacity: 0.9,
                fontSize: '0.6rem',
                letterSpacing: '0.3px',
                display: 'block',
              }}
            >
              Assistant for Reliable Insurance AI
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={handleSidebarCollapse}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'scale(1.05)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {Object.entries(groupedItems).map(([category, items]) => (
          <Box key={category} sx={{ mb: 2 }}>
            {!sidebarCollapsed && (
              <Box sx={{ px: 3, py: 1.5 }}>
                <Typography
                  variant='overline'
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                  }}
                >
                  {category === 'main'
                    ? t('navigation.categories.main')
                    : category === 'management'
                      ? t('navigation.categories.management')
                      : category === 'content'
                        ? t('navigation.categories.content')
                        : category === 'ai'
                          ? t('navigation.categories.aiServices')
                          : category}
                </Typography>
              </Box>
            )}
            <List sx={{ py: 0 }}>
              {items.map(item => (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip
                    title={sidebarCollapsed && !isMobile ? item.text : ''}
                    placement='right'
                    disableHoverListener={!sidebarCollapsed || isMobile}
                  >
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile) dispatch(setSidebarOpen(false));
                      }}
                      sx={{
                        minHeight: 52,
                        mx: 1.5,
                        borderRadius: 2,
                        justifyContent:
                          sidebarCollapsed && !isMobile
                            ? 'center'
                            : 'flex-start',
                        px: sidebarCollapsed && !isMobile ? 2 : 3,
                        py: 1.5,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)',
                        '&.Mui-selected': {
                          background: PRIMARY.gradient,
                          color: 'white',
                          boxShadow: SHADOWS.gradient,
                          border: GLASS.border(theme.palette.mode),
                          '&:hover': {
                            background: PRIMARY.gradientReverse,
                            transform: 'translateX(4px) scale(1.02)',
                            boxShadow: SHADOWS.cardHover,
                          },
                          '& .MuiListItemIcon-root': {
                            color: 'white',
                          },
                        },
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'light'
                              ? 'rgba(103, 126, 234, 0.08)'
                              : 'rgba(103, 126, 234, 0.12)',
                          transform: 'translateX(2px)',
                          boxShadow: SHADOWS.card,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          color: 'inherit',
                          minWidth: sidebarCollapsed && !isMobile ? 0 : 40,
                          mr: sidebarCollapsed && !isMobile ? 0 : 2.5,
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {(!sidebarCollapsed || isMobile) && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            letterSpacing: '0.2px',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      {/* Footer with Version */}
      <Box sx={{ mt: 'auto' }}>
        <Divider
          sx={{
            borderColor: BORDERS.light,
          }}
        />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              theme.palette.mode === 'light'
                ? GLASS.background(theme.palette.mode)
                : DARK_BG.radial,
            backdropFilter: GLASS.backdropFilter,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background:
                theme.palette.mode === 'light'
                  ? `linear-gradient(90deg, transparent 0%, ${PRIMARY.main} 50%, transparent 100%)`
                  : `linear-gradient(90deg, transparent 0%, ${PRIMARY.light} 50%, transparent 100%)`,
              opacity: 0.5,
            },
          }}
        >
          {!sidebarCollapsed && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: GLASS.background(theme.palette.mode),
                border: GLASS.border(theme.palette.mode),
                boxShadow: SHADOWS.card,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  background: GLASS.background(theme.palette.mode),
                  transform: 'translateY(-1px)',
                  boxShadow: SHADOWS.gradient,
                },
              }}
            >
              <Info
                sx={{
                  fontSize: 18,
                  color:
                    theme.palette.mode === 'light'
                      ? 'primary.main'
                      : 'primary.light',
                }}
              />
              <Typography
                variant='caption'
                sx={{
                  color:
                    theme.palette.mode === 'light'
                      ? 'primary.main'
                      : 'primary.light',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  background: PRIMARY.gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                v{version}
              </Typography>
            </Box>
          )}
          {sidebarCollapsed && (
            <Tooltip title={`Version ${version}`} placement='right'>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Chip
                  label={version}
                  size='small'
                  sx={{
                    fontSize: '0.65rem',
                    height: 24,
                    fontWeight: 600,
                    background: PRIMARY.gradient,
                    color: 'white',
                    border: GLASS.border(theme.palette.mode),
                    boxShadow: SHADOWS.gradient,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: SHADOWS.cardHover,
                    },
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          background: GLASS.background(theme.palette.mode),
          backdropFilter: GLASS.backdropFilter,
          borderBottom: GLASS.border(theme.palette.mode),
          display: chatFullscreen ? 'none' : 'flex',
          color: 'text.primary',
          boxShadow: SHADOWS.glass(theme.palette.mode),
          transition: theme.transitions.create(
            ['margin', 'width', 'background', 'box-shadow'],
            {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }
          ),
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: PRIMARY.gradient,
            opacity: 0.8,
          },
          '&:hover': {
            background: GLASS.background(theme.palette.mode),
            boxShadow: SHADOWS.cardHover,
          },
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64, md: 72 },
            px: { xs: 1, sm: 2 },
          }}
        >
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{
              mr: { xs: 1, sm: 2 },
              display: { md: 'none' },
              p: 1,
              borderRadius: 2,
              background: GLASS.background(theme.palette.mode),
              border: GLASS.border(theme.palette.mode),
              '&:hover': {
                background: GLASS.background(theme.palette.mode),
                transform: 'scale(1.05)',
                boxShadow: SHADOWS.card,
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', sm: 'block' },
                width: { sm: 48, md: 64 },
                height: { sm: 48, md: 64 },
                flexShrink: 0,
              }}
            >
              <img
                src='/android-chrome-192x192.png'
                alt='ARIA Logo'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography
              variant='h6'
              component='div'
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: '120px', sm: '200px', md: 'none' },
              }}
            >
              {navigationItems.find(item => item.path === location.pathname)
                ?.text || t('navigation.dashboard')}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1, md: 2 },
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <LanguageSwitcher />
            </Box>

            <Tooltip
              title={
                themeMode === 'light'
                  ? t('common.switchToDark')
                  : t('common.switchToLight')
              }
            >
              <IconButton
                onClick={() =>
                  dispatch(setTheme(themeMode === 'light' ? 'dark' : 'light'))
                }
                sx={{
                  borderRadius: 2,
                  p: { xs: 0.5, sm: 1 },
                  background: GLASS.background(theme.palette.mode),
                  border: GLASS.border(theme.palette.mode),
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    background: GLASS.background(theme.palette.mode),
                    transform: 'scale(1.05)',
                    boxShadow: SHADOWS.cardHover,
                  },
                }}
                color='inherit'
              >
                {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1, md: 1.5 },
              }}
            >
              <Tooltip title={t('navigation.account')}>
                <IconButton
                  onClick={() => navigate('/account')}
                  sx={{
                    borderRadius: 2,
                    p: { xs: 0.5, sm: 1 },
                    background: GLASS.background(theme.palette.mode),
                    border: GLASS.border(theme.palette.mode),
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      background: GLASS.background(theme.palette.mode),
                      transform: 'scale(1.05)',
                      boxShadow: SHADOWS.cardHover,
                    },
                  }}
                  color='inherit'
                >
                  <Settings />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1, md: 1.5 },
                  px: { xs: 1, sm: 1.5, md: 2 },
                  py: { xs: 0.5, sm: 0.75, md: 1 },
                  borderRadius: 3,
                  background: GLASS.background(theme.palette.mode),
                  backdropFilter: GLASS.backdropFilter,
                  border: GLASS.border(theme.palette.mode),
                  boxShadow: SHADOWS.card,
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    background: GLASS.background(theme.palette.mode),
                    transform: 'translateY(-2px)',
                    boxShadow: SHADOWS.gradient,
                  },
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    maxWidth: { xs: '60px', sm: '100px', md: 'none' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.username}
                </Typography>
                <IconButton
                  size='small'
                  edge='end'
                  aria-label='account of current user'
                  aria-controls='menu-appbar'
                  aria-haspopup='true'
                  onClick={handleProfileMenuOpen}
                  sx={{
                    p: 0.5,
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      background: PRIMARY.gradient,
                      boxShadow: SHADOWS.gradient,
                      border: GLASS.border(theme.palette.mode),
                    }}
                  >
                    <AccountCircle sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Avatar>
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component='nav'
        sx={{
          width: { md: currentDrawerWidth },
          flexShrink: { md: 0 },
          display: chatFullscreen ? 'none' : 'block',
        }}
      >
        <Drawer
          variant='temporary'
          open={sidebarOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: chatFullscreen ? 0 : { xs: 1, sm: 2, md: 3 },
          width: chatFullscreen
            ? '100vw'
            : { md: `calc(100% - ${currentDrawerWidth}px)` },
          height: chatFullscreen ? '100vh' : 'auto',
          position: chatFullscreen ? 'fixed' : 'relative',
          top: chatFullscreen ? 0 : 'auto',
          left: chatFullscreen ? 0 : 'auto',
          zIndex: chatFullscreen ? 10000 : 'auto',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {!chatFullscreen && <Toolbar />}
        {children}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem
          onClick={() => {
            navigate('/account');
            handleProfileMenuClose();
          }}
        >
          <ListItemIcon>
            <Settings fontSize='small' />
          </ListItemIcon>
          {t('navigation.account')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize='small' />
          </ListItemIcon>
          {t('navigation.logout')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
